import { useCallback, useEffect, useMemo, useRef, useState, type HTMLAttributes, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { Flex, FlexCenHorVer, FlexCol, FlexColCenVer } from '@/UI/styles';
import GalleryGradientOverlay from '@/components/Common/GalleryGradientOverlay';
import { BufferingIndicator } from '@/components/Common/BufferingIndicator';
import { isWebKitBrowser, unsupportedVideoFormat } from '@/utils/fileUploadUtils';
import { useIsHovered } from '@/hooks/useIsHovered';
import { useBufferingIndicator } from '@/hooks/useBufferingIndicator';
import { useDownscaledImageUrl } from '@/components/Nodes/Shared/useDownscaledImageUrl';
import { AssetOverlay, type AssetOverlayProps } from '@/components/Common/AssetOverlay/AssetOverlay';
import {
  CompactMediaPlayerControls,
  TrimMediaPlayerControls,
} from '@/components/MediaPlayerControls/MediaPlayerControls';
import { PlayFilledIcon } from '@/UI/Icons/PlayFilledIcon';
import { LottieAnimation } from '@/components/Common/LottieAnimation';
import { TextViewer } from '@/components/Common/ImageList/TextViewer';
import { useAppContextMenu } from '@/UI/AppContextMenu/useAppContextMenu';
import { color } from '@/colors';
import { FF_COMPOSITOR_AUDIO_ENGINE } from '@/consts/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useInteractiveDownloader } from '@/hooks/useInteractiveDownloader';
import { useSaveRecipe } from '@/hooks/useSaveRecipe';
import { useIsNodeDiagonalResizeEnabled } from '@/hooks/useIsNodeDiagonalResizeEnabled';
import { useResizeScalerAttributes } from '@/hooks/useNodeResizeStyles';
import { getMediaFramePlaceholderSx } from '@/consts/node-ui.consts';
import { shouldMeasureThreeDViewerOnResize } from '@/consts/node-resize-policy';
import { MENU_NAMES } from '@/consts/menu-names.consts';
import { useResolvedAssets, useWorkflowStore } from '@/state/workflow.state';
import { log } from '@/logger/logger';
import { assetThumbnailUrl, assetUrl } from '@/utils/assetUrls';
import { getFileExtension, getVideoMimeType } from '@/utils/urls';
import { extensionFromMimeType } from '@/utils/mimeTypes';
import { buildExportFileName, downloader } from '@/components/Recipe/NodeAssetDownloader/downloadUtils';
import { useIsVideoFrameLoading } from '@/state/videoFrameLoading.state';
import { trackNodeExportDownload, useNodeExportDownloads } from '@/state/nodeExportDownloads.state';
import { useGlobalStore } from '@/state/global.state';
import { MediaLoadingOverlay } from '@/components/Common/FileUploader/Canvas/MediaLoadingOverlay';
import { getOutputHandle } from '@/components/Nodes/DynamicNode/DynamicNodeHandles';
import { I18N_KEYS } from '@/language/keys';
import { Voice52Icon } from '@/UI/Icons/Voice52Icon';
import {
  useEdges,
  useFlowView,
  useNode,
  useNodeData,
  useNodeDataNullable,
  useNodeNullable,
  useNodeRenderTarget,
  useVideoDependencies,
  useVideoState,
  useVideoView,
} from '../../Recipe/FlowContext';
import { useAudioPlayback } from '../../Recipe/FlowComponents/Editor/Designer/useAudioPlayback';
import { resolveDownloadableVideoAudioFile } from '../../Recipe/Views/ExportView';
import { useNodeImageActions } from './useNodeImageActions';
import NodeErrorOverlay from './NodeErrorOverlay';
import type { TimeDisplayMode } from '@/components/MediaPlayerControls/types';
import type { ActionMenuItem, MenuAction } from '@/components/Menu/Actions';
import type { Dimensions, FileKind, Input, NodeId, NodeType, OutputId } from 'web';
import type { UploadedAsset } from '@/types/api/assets';
import type { BaseNodeData, ImportData, PreviewData } from '@/types/node';
import type { AiModelData } from '@/types/nodes/model';
import type { CompositorNodeV3 } from '@/types/nodes/compositor';

const logger = log.getLogger('FileViewer');

type MediaFrameProps = HTMLAttributes<HTMLElement>;

const AUDIO_PREVIEW_HEIGHT = 144;

function WithFrameOverlay({
  frame,
  frameOverlay,
  mediaFrameProps,
}: {
  frame: React.ReactNode;
  frameOverlay?: React.ReactNode;
  mediaFrameProps?: MediaFrameProps;
}) {
  return (
    <Box {...mediaFrameProps} sx={{ position: 'relative', width: '100%' }}>
      {frame}
      {frameOverlay}
    </Box>
  );
}

function AudioMediaFrame({
  children,
  overlay,
  mediaFrameProps,
  fillMediaFrame = false,
  height = `${AUDIO_PREVIEW_HEIGHT}px`,
}: {
  children: React.ReactNode;
  overlay?: React.ReactNode;
  mediaFrameProps?: MediaFrameProps;
  fillMediaFrame?: boolean;
  height?: number | string;
}) {
  return (
    <FlexCenHorVer
      {...mediaFrameProps}
      className="media-container-dark"
      sx={{
        width: '100%',
        ...(fillMediaFrame ? { flex: 1, minHeight: AUDIO_PREVIEW_HEIGHT } : { height }),
        position: 'relative',
        borderRadius: 2,
        border: `1px solid ${color.White04_T}`,
        overflow: 'hidden',
        backgroundColor: token.bgSelected,
        backgroundImage: 'none',
        color: token.text,
      }}
    >
      {children}
      {overlay}
    </FlexCenHorVer>
  );
}

function VideoViewer({
  id,
  handleContextMenu,
  onVideoPause,
  onVideoScrub,
  disabled,
  clickToPlayDisabled,
  overlay,
  index = 0,
  width,
  height,
  duration,
  hasAudio,
  fps,
  shouldPlayOnHover,
  isHovered,
  suspendMediaPlayback = false,
  mediaFrameProps,
  noBorderRadius,
  frameOverlay,
  playerControls = 'compact',
  timeDisplayMode,
  onTimeDisplayModeChange,
}: {
  id: NodeId;
  handleContextMenu?: (event: MouseEvent<HTMLDivElement>) => void;
  onVideoPause?: (time: number) => void;
  onVideoScrub?: (time: number, ongoing: boolean) => void;
  disabled?: boolean;
  clickToPlayDisabled?: boolean;
  overlay?: React.ReactNode;
  frameOverlay?: React.ReactNode;
  index?: number;
  width: number;
  height: number;
  duration: number;
  hasAudio: boolean;
  fps?: number;
  shouldPlayOnHover?: boolean;
  isHovered?: boolean;
  suspendMediaPlayback?: boolean;
  mediaFrameProps?: MediaFrameProps;
  noBorderRadius?: boolean;
  playerControls?: PlayerControlsVariant;
  timeDisplayMode?: TimeDisplayMode;
  onTimeDisplayModeChange?: (mode: TimeDisplayMode) => void;
}) {
  const canvasRef = useNodeRenderTarget(id, index);
  const { t } = useTranslation();
  const videoView = useVideoView(id, index);
  const isWebKit = isWebKitBrowser();
  const evaluated = useFlowView().evaluateNode(id, index);
  const evaluatedVideo = evaluated?.type === 'video' ? evaluated.value : undefined;
  // Only a computed (e.g. compositor) video lacks its own url; that's the only case that needs
  // the graph-wide dependency walk, and it has no gallery `index` to get confused about.
  const videoDependencies = useVideoDependencies(id, isWebKit && evaluatedVideo?.type === 'computed');
  const dependenciesToCheck = isWebKit && evaluatedVideo?.type === 'file' ? [evaluatedVideo.url] : videoDependencies;
  const unsupportedFormat = unsupportedVideoFormat(dependenciesToCheck);
  const { paused, currentTime, muted, isBuffering } = useVideoState(id, index) ?? {
    paused: true,
    currentTime: 0,
    muted: true,
    isBuffering: false,
  };
  const showBufferingIndicator = useBufferingIndicator(isBuffering);

  // Sync audio layers from upstream compositor with video playback
  const edges = useEdges();
  const sourceCompositorId = useMemo(() => {
    const edge = edges.find((e) => e.target === id);
    return edge?.source;
  }, [edges, id]);
  const sourceData = useNodeDataNullable<CompositorNodeV3>(sourceCompositorId);

  const audioEngineEnabled = useFeatureFlag(FF_COMPOSITOR_AUDIO_ENGINE).value === true;
  useAudioPlayback({
    layers: sourceData?.data?.layers,
    resources: sourceData?.data?.resources,
    layerOrder: sourceData?.data?.layerOrder,
    compositorInputs: sourceData?.data?.input,
    currentTime,
    isPlaying: !paused,
    isBuffering,
    globalMuted: muted,
    enabled: !audioEngineEnabled,
  });

  // Handle video play on hover when shouldPlayOnHover is enabled
  useEffect(() => {
    if (!shouldPlayOnHover) {
      return;
    }
    if (suspendMediaPlayback) {
      videoView.pauseIfPlaying();
      return;
    }
    if (isHovered && paused) {
      videoView.togglePlay();
    } else if (!isHovered && !paused) {
      videoView.pauseIfPlaying();
    }
  }, [isHovered, shouldPlayOnHover, paused, videoView, suspendMediaPlayback]);

  const handleTogglePlay = useCallback(() => {
    if (disabled) return;
    if (!paused) {
      onVideoPause?.(currentTime);
    }
    videoView.togglePlay();
  }, [onVideoPause, videoView, paused, currentTime, disabled]);

  const handleToggleMute = useCallback(() => {
    if (disabled) return;
    videoView.toggleMute();
  }, [videoView, disabled]);

  const handleScrub = useCallback(
    (time: number, ongoing: boolean) => {
      if (disabled) return;
      if (ongoing) {
        videoView.pauseIfPlaying();
      }
      onVideoScrub?.(time, ongoing);
      videoView.setTime(time, ongoing);
    },
    [onVideoScrub, videoView, disabled],
  );

  return (
    <FlexColCenVer>
      <WithFrameOverlay
        frameOverlay={frameOverlay}
        mediaFrameProps={mediaFrameProps}
        frame={
          <FlexCenHorVer
            className="media-container-dark"
            onContextMenu={handleContextMenu}
            onClick={shouldPlayOnHover || clickToPlayDisabled ? undefined : handleTogglePlay}
            sx={{
              cursor: clickToPlayDisabled ? undefined : 'pointer',
              position: 'relative',
              borderRadius: noBorderRadius ? 0 : 2,
              overflow: 'hidden',
            }}
          >
            <canvas
              ref={canvasRef}
              draggable="false"
              style={{ width: '100%', aspectRatio: `${width}/${height}`, position: 'relative' }}
            />
            {unsupportedFormat && (
              <NodeErrorOverlay
                errorMessage={t(I18N_KEYS.COMMON_COMPONENTS.FILE_UPLOADER.VIDEO_FORMAT_UNSUPPORTED_IN_BROWSER, {
                  format: unsupportedFormat,
                })}
              />
            )}
            {overlay}
            <AnimatePresence>
              {showBufferingIndicator && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.1, ease: 'easeInOut' }}
                  style={{
                    position: 'absolute',
                    top: 12,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <BufferingIndicator />
                </motion.div>
              )}
            </AnimatePresence>
            {shouldPlayOnHover && !isHovered && (
              <Box
                sx={{
                  position: 'absolute',
                }}
              >
                <PlayFilledIcon />
              </Box>
            )}
          </FlexCenHorVer>
        }
      />

      {!shouldPlayOnHover && (
        <FlexColCenVer sx={{ gap: 1 }}>
          {playerControls === 'trim' ? (
            <TrimMediaPlayerControls
              currentTime={currentTime}
              duration={duration}
              fps={fps}
              isPaused={paused}
              isMuted={muted}
              hasAudio={hasAudio}
              timeDisplayMode={timeDisplayMode}
              onTimeDisplayModeChange={onTimeDisplayModeChange}
              onTogglePlay={handleTogglePlay}
              onSetTime={handleScrub}
              onToggleMute={handleToggleMute}
            />
          ) : (
            <CompactMediaPlayerControls
              scrubberPosition={playerControls === 'compact-bottom' ? 'bottom' : 'top'}
              currentTime={currentTime}
              duration={duration}
              fps={fps}
              isPaused={paused}
              isMuted={muted}
              hasAudio={hasAudio}
              onTogglePlay={handleTogglePlay}
              onSetTime={handleScrub}
              onToggleMute={handleToggleMute}
            />
          )}
        </FlexColCenVer>
      )}
    </FlexColCenVer>
  );
}

export function ThreeDViewer({
  id,
  index,
  handleContextMenu,
  overlay,
  allowInteraction = true,
  mediaFrameProps,
}: {
  id: NodeId;
  index?: number;
  handleContextMenu?: (event: MouseEvent<HTMLDivElement>) => void;
  overlay?: React.ReactNode;
  allowInteraction?: boolean;
  mediaFrameProps?: MediaFrameProps;
}) {
  const canvasRef = useNodeRenderTarget(id, index ?? 0);
  const nodeType = useNodeNullable(id)?.type;
  const resizeScalerAttributes = useResizeScalerAttributes(shouldMeasureThreeDViewerOnResize(nodeType));

  const nodeData = useNodeDataNullable<ImportData | AiModelData>(id);
  const fallbackDimensions: Dimensions =
    nodeData && 'renderWidth' in nodeData
      ? { width: nodeData.renderWidth, height: nodeData.renderHeight }
      : { width: nodeData?.width ?? 1024, height: nodeData?.height ?? 1024 };
  const selectedIndex = index ?? nodeData?.selectedIndex ?? 0;
  const threeDFile =
    nodeData && 'renderWidth' in nodeData
      ? nodeData.generations?.[selectedIndex]?.kind
      : nodeData?.files?.[selectedIndex];
  const viewport =
    threeDFile?.type === '3D' ? (threeDFile.cameraOptions?.viewport ?? fallbackDimensions) : fallbackDimensions;
  const aspectRatio = viewport.width / viewport.height;

  const rightMouseDownRef = useRef<{ x: number; y: number; timestamp: number } | null>(null);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    const isRightButtonHeld = (event.buttons & 2) !== 0;
    if (!isRightButtonHeld) return;
    rightMouseDownRef.current = { x: event.clientX, y: event.clientY, timestamp: event.timeStamp };
  };

  const handleMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    if (rightMouseDownRef.current === null) return;
    const mouseMoved =
      Math.hypot(event.clientX - rightMouseDownRef.current.x, event.clientY - rightMouseDownRef.current.y) > 4;
    const clickTime = event.timeStamp - rightMouseDownRef.current.timestamp;
    if (event.button === 2 && !mouseMoved && clickTime <= 666) {
      handleContextMenu?.(event);
    }
    if (event.button === 2) {
      rightMouseDownRef.current = null;
    }
  };

  const handleContextMenuInternal = (event: React.MouseEvent<HTMLDivElement>) => {
    // accessibility: only block mouse, allow keyboard context menu
    if (event.button === 2) {
      event.preventDefault();
      return;
    }
    handleContextMenu?.(event);
  };

  return (
    <FlexCenHorVer
      {...mediaFrameProps}
      {...resizeScalerAttributes}
      className="media-container-dark nodrag nopan nowheel"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onContextMenu={handleContextMenuInternal} // right click used to move the object in 3D viewer. Custom implementation of context menu required.
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        pointerEvents: allowInteraction ? 'auto' : 'none',
      }}
    >
      <canvas
        ref={canvasRef}
        draggable="false"
        style={{
          width: '100%',
          aspectRatio: `${aspectRatio}`,
          position: 'relative',
        }}
      />
      {overlay}
    </FlexCenHorVer>
  );
}

export function ImageViewer({
  id,
  width,
  height,
  index,
  handleContextMenu,
  overlay,
  noBorderRadius,
  mediaFrameProps,
  frameOverlay,
}: {
  id: NodeId;
  width: number;
  height: number;
  index?: number;
  handleContextMenu?: (event: MouseEvent<HTMLDivElement>) => void;
  overlay?: React.ReactNode;
  noBorderRadius?: boolean;
  mediaFrameProps?: MediaFrameProps;
  frameOverlay?: React.ReactNode;
}) {
  const canvasRef = useNodeRenderTarget(id, index ?? 0);

  return (
    <WithFrameOverlay
      frameOverlay={frameOverlay}
      mediaFrameProps={mediaFrameProps}
      frame={
        <FlexCenHorVer
          className="media-container-dark"
          onContextMenu={handleContextMenu}
          sx={{ position: 'relative', borderRadius: noBorderRadius ? 0 : 2, overflow: 'hidden' }}
        >
          <canvas
            ref={canvasRef}
            draggable="false"
            style={{
              width: '100%',
              aspectRatio: `${width}/${height}`,
              position: 'relative',
            }}
          />
          {overlay}
        </FlexCenHorVer>
      }
    />
  );
}

export function ImageFileViewer({
  url,
  thumbnailUrl,
  width,
  height,
  handleContextMenu,
  overlay,
  mediaFrameProps,
}: {
  url: string;
  thumbnailUrl?: string;
  width: number;
  height: number;
  handleContextMenu?: (event: MouseEvent<HTMLDivElement>) => void;
  overlay?: React.ReactNode;
  mediaFrameProps?: MediaFrameProps;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [isThumbnailLoading, setIsThumbnailLoading] = useState(true);
  const isThumbnailSource = url === thumbnailUrl;
  const placeholderUrl = isThumbnailSource ? undefined : thumbnailUrl;
  const displayUrl = useDownscaledImageUrl(url, isThumbnailSource ? undefined : { width, height });
  const mediaStyle = {
    width: '100%',
    aspectRatio: `${width}/${height}`,
    position: 'relative' as const,
  };
  return (
    <FlexCenHorVer
      {...mediaFrameProps}
      className="media-container-dark"
      onContextMenu={handleContextMenu}
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {placeholderUrl && isLoading ? (
        <img
          crossOrigin="anonymous"
          draggable="false"
          src={placeholderUrl}
          onLoad={() => setIsThumbnailLoading(false)}
          onError={() => setIsThumbnailLoading(false)}
          style={{
            ...mediaStyle,
            backgroundColor: isThumbnailLoading ? 'rgba(255, 255, 255, 0.08)' : undefined,
          }}
        />
      ) : (
        <></>
      )}
      <img
        crossOrigin="anonymous"
        hidden={isLoading && placeholderUrl !== undefined}
        onLoad={() => setIsLoading(false)}
        onError={() => setIsLoading(false)}
        draggable="false"
        src={displayUrl}
        style={{
          ...mediaStyle,
          backgroundColor: isLoading ? 'rgba(255, 255, 255, 0.08)' : undefined,
        }}
      />
      {overlay}
    </FlexCenHorVer>
  );
}

function AudioPreview({
  url,
  overlay,
  playOnHover,
  isHovered,
  fixedItemSize,
  fillMediaFrame = false,
  suspendMediaPlayback = false,
  mediaFrameProps,
}: {
  url: string;
  overlay?: React.ReactNode;
  playOnHover?: boolean;
  isHovered?: boolean;
  fixedItemSize?: number;
  fillMediaFrame?: boolean;
  suspendMediaPlayback?: boolean;
  mediaFrameProps?: MediaFrameProps;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  // Play/Pause toggle
  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      void audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Jump to specific time (in seconds)
  const setAudioTime = (time: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = time;
    setCurrentTime(time);
  };

  // Update current time as audio plays
  const handleTimeUpdate = () => {
    setCurrentTime(audioRef.current?.currentTime ?? 0);
  };

  // Set duration when metadata loads
  const handleLoadedMetadata = () => {
    setDuration(audioRef.current?.duration ?? 0);
  };

  const toggleMuted = () => {
    if (!audioRef.current) return;
    setMuted(!muted);
    audioRef.current.muted = !muted;
  };

  function handleEnded(): void {
    setIsPlaying(false);
    setAudioTime(0);
  }
  // handle audio play on hover
  useEffect(() => {
    if (!playOnHover || !audioRef.current) {
      return;
    }
    if (suspendMediaPlayback || !isHovered) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }
    void audioRef.current.play().catch(() => undefined);
  }, [playOnHover, isHovered, suspendMediaPlayback]);

  return (
    <FlexColCenVer sx={fillMediaFrame ? { flex: 1, minHeight: 0, width: '100%' } : undefined}>
      {!playOnHover ? (
        <>
          <AudioMediaFrame overlay={overlay} mediaFrameProps={mediaFrameProps} fillMediaFrame={fillMediaFrame}>
            <Voice52Icon aria-hidden="true" />
            <audio
              ref={audioRef}
              draggable="false"
              crossOrigin="anonymous"
              src={url}
              onEnded={handleEnded}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              style={{
                display: 'none',
              }}
            />
          </AudioMediaFrame>
          <CompactMediaPlayerControls
            currentTime={currentTime}
            duration={duration}
            isPaused={!isPlaying}
            isMuted={muted}
            hasAudio={true}
            showFrameStepButtons={false}
            timeInputVariant="time"
            onTogglePlay={togglePlayPause}
            onSetTime={setAudioTime}
            onToggleMute={toggleMuted}
          />
        </>
      ) : (
        <AudioMediaFrame
          overlay={overlay}
          mediaFrameProps={mediaFrameProps}
          height={fixedItemSize ? `${fixedItemSize}px` : '100%'}
        >
          <audio ref={audioRef} draggable="false" crossOrigin="anonymous" src={url} loop={true} />
          {isHovered ? (
            <LottieAnimation width={42} height={42} loop={true} src="/audio-wave.lottie" />
          ) : (
            <Voice52Icon aria-hidden="true" />
          )}
        </AudioMediaFrame>
      )}
    </FlexColCenVer>
  );
}

export interface FileViewerProps {
  id: NodeId;
  index?: number;
  asset?: UploadedAsset | FileKind;
  handleContextMenu?: (event: MouseEvent<HTMLDivElement>) => void;
  isClickable?: boolean;
  isMultiFilesViewMode?: boolean;
  isSelected?: boolean;
  mediaFrameOverlay?: React.ReactNode;
  mediaFrameCover?: React.ReactNode;
  mediaFrameFooter?: React.ReactNode;
  fixedItemSize?: number;
  onTextChange?: (index: number, value: string) => void;
  hasMultipleFiles?: boolean;
  suspendMediaPlayback?: boolean;
  fillMediaFrame?: boolean;
}

export function FileViewer({
  id,
  index,
  asset,
  handleContextMenu,
  isClickable,
  isMultiFilesViewMode = false,
  isSelected = false,
  mediaFrameOverlay,
  mediaFrameCover,
  mediaFrameFooter,
  hasMultipleFiles = false,
  fixedItemSize,
  onTextChange,
  suspendMediaPlayback = false,
  fillMediaFrame = false,
}: FileViewerProps) {
  const { isHovered, ...hoveredProps } = useIsHovered();
  const resolvedAssets = useResolvedAssets();
  const isVideoFrameLoading = useIsVideoFrameLoading(asset?.type === 'video' ? asset.url : undefined);
  const overlay =
    mediaFrameOverlay || mediaFrameCover ? (
      <>
        {mediaFrameOverlay ? (
          <GalleryGradientOverlay isSelected={isHovered}>{mediaFrameOverlay}</GalleryGradientOverlay>
        ) : null}
        {mediaFrameCover}
      </>
    ) : null;

  return (
    <FlexCol
      sx={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        cursor: isClickable ? 'pointer' : 'default',
      }}
      className="nodrag"
    >
      {asset?.type === 'video' ? (
        <VideoViewer
          id={id}
          isHovered={isHovered}
          mediaFrameProps={hoveredProps}
          index={index}
          handleContextMenu={handleContextMenu}
          overlay={
            <>
              {isVideoFrameLoading && <MediaLoadingOverlay clickThrough />}
              {overlay}
            </>
          }
          disabled={isVideoFrameLoading}
          width={asset.width ?? 1}
          height={asset.height ?? 1}
          duration={asset.duration ?? 0}
          hasAudio={asset.hasAudio ?? true}
          fps={asset.fps}
          shouldPlayOnHover={isMultiFilesViewMode}
          suspendMediaPlayback={suspendMediaPlayback}
        />
      ) : asset?.type === 'image' ? (
        (() => {
          const url =
            isMultiFilesViewMode && !isSelected
              ? (assetThumbnailUrl(asset, resolvedAssets) ?? assetUrl(asset, resolvedAssets) ?? asset.url)
              : (assetUrl(asset, resolvedAssets) ?? asset.url);

          return (
            <ImageFileViewer
              key={asset.url}
              url={url}
              thumbnailUrl={assetThumbnailUrl(asset, resolvedAssets, { thumbnailOnly: true })}
              width={asset.width ?? 1}
              height={asset.height ?? 1}
              handleContextMenu={handleContextMenu}
              overlay={overlay}
              mediaFrameProps={hoveredProps}
            />
          );
        })()
      ) : asset?.type === '3D' ? (
        <ThreeDViewer
          id={id}
          index={index}
          handleContextMenu={handleContextMenu}
          overlay={overlay}
          allowInteraction={!isMultiFilesViewMode}
          mediaFrameProps={hoveredProps}
        />
      ) : asset?.type === 'audio' ? (
        <AudioPreview
          key={asset.url}
          url={assetUrl(asset, resolvedAssets) ?? asset.url}
          overlay={overlay}
          playOnHover={isMultiFilesViewMode}
          isHovered={isHovered}
          fixedItemSize={fixedItemSize}
          fillMediaFrame={fillMediaFrame}
          suspendMediaPlayback={suspendMediaPlayback}
          mediaFrameProps={hoveredProps}
        />
      ) : asset?.type === 'text' ? (
        <Flex {...hoveredProps} sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
          <TextViewer
            file={asset}
            index={index ?? 0}
            isMultiFilesViewMode={isMultiFilesViewMode}
            hasMultipleFiles={hasMultipleFiles}
            fixedItemSize={fixedItemSize}
            onTextChange={onTextChange}
            disableTooltip={suspendMediaPlayback}
          />
          {overlay}
        </Flex>
      ) : (
        <FlexCol
          className="media-container-dark"
          sx={{
            position: 'relative',
            width: '100%',
            ...(fillMediaFrame ? { flex: 1, minHeight: 430 } : { height: '430px' }),
          }}
        >
          {overlay}
        </FlexCol>
      )}
      {mediaFrameFooter}
    </FlexCol>
  );
}

function ComputedAudioViewer({
  id,
  duration,
  overlay,
  mediaFrameProps,
}: {
  id: NodeId;
  duration: number;
  overlay?: React.ReactNode;
  mediaFrameProps?: MediaFrameProps;
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [muted, setMuted] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startRef = useRef(0);

  // Find source compositor for audio layer data
  const edges = useEdges();
  const sourceCompositorId = useMemo(() => {
    const edge = edges.find((e) => e.target === id);
    return edge?.source;
  }, [edges, id]);
  const sourceData = useNodeDataNullable<CompositorNodeV3>(sourceCompositorId);

  // Legacy playback path, used only when the audio engine flag is off. When it is
  // on, AudioNodeViewer drives playback through the node's VideoTool instead.
  useAudioPlayback({
    layers: sourceData?.data?.layers,
    resources: sourceData?.data?.resources,
    layerOrder: sourceData?.data?.layerOrder,
    compositorInputs: sourceData?.data?.input,
    currentTime,
    isPlaying,
    globalMuted: muted,
    enabled: true,
  });

  // Advance currentTime while playing
  useEffect(() => {
    if (isPlaying) {
      startRef.current = performance.now() - currentTime * 1000;
      intervalRef.current = setInterval(() => {
        const elapsed = (performance.now() - startRef.current) / 1000;
        if (elapsed >= duration) {
          setCurrentTime(0);
          setIsPlaying(false);
        } else {
          setCurrentTime(elapsed);
        }
      }, 50);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // currentTime intentionally excluded — including it would restart the interval every 50ms.
    // Scrubbing updates startRef directly via handleScrub instead.
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, [isPlaying, duration]);

  const handleTogglePlay = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const handleToggleMute = useCallback(() => {
    setMuted((prev) => !prev);
  }, []);

  const handleScrub = useCallback((time: number) => {
    setCurrentTime(time);
    startRef.current = performance.now() - time * 1000;
  }, []);

  return (
    <FlexColCenVer>
      <AudioMediaFrame overlay={overlay} mediaFrameProps={mediaFrameProps}>
        <Voice52Icon aria-hidden="true" />
      </AudioMediaFrame>
      <CompactMediaPlayerControls
        currentTime={currentTime}
        duration={duration}
        isPaused={!isPlaying}
        isMuted={muted}
        hasAudio={true}
        showFrameStepButtons={false}
        timeInputVariant="time"
        onTogglePlay={handleTogglePlay}
        onSetTime={handleScrub}
        onToggleMute={handleToggleMute}
      />
    </FlexColCenVer>
  );
}

/**
 * Plays a computed-audio node through the shared playback path: the node's
 * `VideoTool` owns the clock and drives the audio engine (same as video), so this
 * reads `VideoState` and drives it via `useVideoView` — no local clock, no direct
 * engine calls. Used when the audio engine flag is on; `ComputedAudioViewer` is
 * the flag-off fallback.
 */
function AudioNodeViewer({
  id,
  duration,
  overlay,
  mediaFrameProps,
  playerControls = 'compact',
  timeDisplayMode,
  onTimeDisplayModeChange,
}: {
  id: NodeId;
  duration: number;
  overlay?: React.ReactNode;
  mediaFrameProps?: MediaFrameProps;
  playerControls?: PlayerControlsVariant;
  timeDisplayMode?: TimeDisplayMode;
  onTimeDisplayModeChange?: (mode: TimeDisplayMode) => void;
}) {
  const videoView = useVideoView(id, 0);
  const { paused, currentTime, muted } = useVideoState(id, 0) ?? {
    paused: true,
    currentTime: 0,
    muted: false,
  };

  const handleTogglePlay = useCallback(() => videoView.togglePlay(), [videoView]);
  const handleToggleMute = useCallback(() => videoView.toggleMute(), [videoView]);
  const handleScrub = useCallback((time: number, ongoing: boolean) => videoView.setTime(time, ongoing), [videoView]);

  return (
    <FlexColCenVer>
      <AudioMediaFrame overlay={overlay} mediaFrameProps={mediaFrameProps}>
        <Voice52Icon aria-hidden="true" />
      </AudioMediaFrame>
      {playerControls === 'trim' ? (
        <TrimMediaPlayerControls
          currentTime={currentTime}
          duration={duration}
          isPaused={paused}
          isMuted={muted}
          hasAudio={true}
          showFrameStepButtons={false}
          timeInputVariant="time"
          timeDisplayMode={timeDisplayMode}
          onTimeDisplayModeChange={onTimeDisplayModeChange}
          onTogglePlay={handleTogglePlay}
          onSetTime={handleScrub}
          onToggleMute={handleToggleMute}
        />
      ) : (
        <CompactMediaPlayerControls
          scrubberPosition={playerControls === 'compact-bottom' ? 'bottom' : 'top'}
          currentTime={currentTime}
          duration={duration}
          isPaused={paused}
          isMuted={muted}
          hasAudio={true}
          showFrameStepButtons={false}
          timeInputVariant="time"
          onTogglePlay={handleTogglePlay}
          onSetTime={handleScrub}
          onToggleMute={handleToggleMute}
        />
      )}
    </FlexColCenVer>
  );
}

/*
 * Which player-controls layout a media preview renders. `'compact'` places the scrubber above the
 * control row. `'compact-bottom'` places it below. `'trim'` drops the scrubber, for nodes that own a
 * richer scrubbing surface of their own.
 */
type PlayerControlsVariant = 'compact' | 'compact-bottom' | 'trim';

interface NodeViewerProps {
  id: NodeId;
  contextMenuItems?: MenuAction[];
  onVideoPause?: (time: number) => void;
  onVideoScrub?: (time: number, ongoing: boolean) => void;
  disabled?: boolean;
  clickToPlayDisabled?: boolean;
  cursor?: string;
  noBorderRadius?: boolean;
  hideOverlay?: boolean;
  mediaFrameOverlay?: React.ReactNode;
  playerControls?: PlayerControlsVariant;
  /** Only meaningful when `playerControls === 'trim'`; keeps the player's timecode/frames toggle in sync with the Trim node's own Start/End fields. */
  timeDisplayMode?: TimeDisplayMode;
  onTimeDisplayModeChange?: (mode: TimeDisplayMode) => void;
}

export function NodeViewer({
  id,
  contextMenuItems,
  onVideoPause,
  onVideoScrub,
  disabled,
  clickToPlayDisabled,
  noBorderRadius,
  hideOverlay,
  cursor = 'default',
  mediaFrameOverlay,
  playerControls,
  timeDisplayMode,
  onTimeDisplayModeChange,
}: NodeViewerProps) {
  const { t } = useTranslation();
  const flowView = useFlowView();
  const file = flowView.evaluateNode(id);
  const resolvedAssets = useResolvedAssets();
  const outputIds = flowView.getOutputIds(id);
  const canCompareInput = useMemo(() => flowView.getCanCompareInput(id), [flowView, id]);
  const audioEngineEnabled = useFeatureFlag(FF_COMPOSITOR_AUDIO_ENGINE).value === true;
  const canDownloadNode = flowView.getCanDownloadNode(id);
  const getDownloadName = useCallback(
    (label?: string) => (file?.type === 'video' ? (label ? `${label} frame` : 'frame') : (label ?? 'image')),
    [file?.type],
  );
  const { isHovered: isMediaFrameHovered, ...mediaFrameProps } = useIsHovered();

  const nodeName = useNodeData<BaseNodeData>(id).name;
  const nodeType = useNode(id).type;
  const isDiagonalResizeEnabled = useIsNodeDiagonalResizeEnabled();
  const nodeOutputType = file?.type;
  const workflowName = useWorkflowStore((state) => state.recipe?.name);
  const workflowId = useWorkflowStore((state) => state.recipe?.id);
  const nodeExportDownloadKey = `${workflowId}/${id}`;
  const isNodeExportDownloading = useNodeExportDownloads((state) => state.downloads.has(nodeExportDownloadKey));
  const saveRecipe = useSaveRecipe();
  const { downloadAssetFromNode } = useInteractiveDownloader();

  const { downloadNodeImage, copyNodeImage } = useNodeImageActions(id);

  const downloadNodeVideo = useCallback(
    async (outputId?: OutputId): Promise<void> => {
      const evaluated = flowView.evaluateNode(id);
      if (evaluated?.type !== 'video') {
        // Not actually a video output — fall back to a frame snapshot.
        await downloadNodeImage(outputId);
        return;
      }
      if (!workflowId) {
        logger.error('Cannot download video without a workflow id', { nodeId: id });
        return;
      }
      return trackNodeExportDownload(nodeExportDownloadKey, nodeName || id, async (signal) => {
        let handle: FileSystemFileHandle | null = null;
        if ('showSaveFilePicker' in window) {
          const storedVideo = evaluated.value.type === 'file' ? evaluated.value : undefined;
          const extension = storedVideo?.mime
            ? extensionFromMimeType(storedVideo.mime, 'video')
            : getFileExtension(storedVideo?.url ?? '', 'video');
          try {
            handle = await downloader.openSavePicker({
              suggestedName: buildExportFileName({ prefix: workflowName, nodeName: nodeName || id, extension }),
              fileType: 'Video',
              mimeType: storedVideo?.mime ?? getVideoMimeType(storedVideo?.url) ?? 'video/mp4',
            });
            if (handle === null) return;
          } catch (error) {
            logger.warn('Save picker failed, falling back to download', error);
          }
        }
        signal.throwIfAborted();
        const input =
          nodeType === 'preview' && evaluated.value.type === 'computed'
            ? (flowView.getNode(id).data as PreviewData).inputNode
            : ({ nodeId: id, outputId: outputId ?? flowView.getOutputIds(id)[0] } as Input);
        if (!input) throw new Error('Preview input not found');
        const { fileInfo, error } = await resolveDownloadableVideoAudioFile(
          evaluated,
          input,
          workflowId,
          saveRecipe,
          resolvedAssets,
          'mp4',
          signal,
          handle ? 'stream' : 'blob',
        );
        signal.throwIfAborted();
        if (error || !fileInfo) throw new Error(error ?? 'File not found');
        if (handle) {
          let stream = fileInfo.stream;
          if (!stream && fileInfo.url) {
            const response = await fetch(fileInfo.url, { signal });
            if (!response.ok) throw new Error(`Failed to fetch file: ${response.statusText}`);
            stream = response.body ?? undefined;
          }
          if (!stream) throw new Error('File not found');
          signal.throwIfAborted();
          await downloader.writeStreamToHandle(handle, stream);
        } else {
          await downloadAssetFromNode({
            ...fileInfo,
            fileType: 'video',
            nodeName: nodeName || id,
            prefix: workflowName,
            signal,
          });
        }
      }).catch((error) => {
        logger.error('Failed to download node video', { nodeId: id, error });
        useGlobalStore.getState().updateSnackbarData({
          text: t(I18N_KEYS.RECIPE_MAIN.ERRORS.DOWNLOAD_FAILED),
          isOpen: true,
          severity: 'error',
        });
      });
    },
    [
      flowView,
      id,
      nodeType,
      workflowId,
      nodeExportDownloadKey,
      t,
      saveRecipe,
      resolvedAssets,
      nodeName,
      workflowName,
      downloadAssetFromNode,
      downloadNodeImage,
    ],
  );

  const downloadMenuItems = useMemo<ActionMenuItem[]>(() => {
    if (!canDownloadNode) return [];
    return (outputIds.length ? outputIds : [undefined]).flatMap((outputId) => {
      const label = outputIds.length > 1 ? getOutputHandle(nodeType as NodeType, outputId)?.label : undefined;
      const biEventKey = {
        menu: MENU_NAMES.FILE_VIEWER,
        action: outputIds.length ? 'download_output_preview' : 'download_preview',
      };
      return [
        {
          biEventKey,
          name: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.DOWNLOAD_IMAGE_PREVIEW, {
            name: getDownloadName(label),
          }),
          action: () => downloadNodeImage(outputId),
        },
        ...(file?.type === 'video'
          ? [
              {
                biEventKey,
                name: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.DOWNLOAD_IMAGE_PREVIEW, {
                  name: label ? `${label} video` : 'video',
                }),
                disabled: isNodeExportDownloading,
                disabledTooltip: t(I18N_KEYS.GENERAL.DOWNLOADING),
                action: () => downloadNodeVideo(outputId),
              },
            ]
          : []),
      ];
    });
  }, [
    canDownloadNode,
    outputIds,
    nodeType,
    file?.type,
    isNodeExportDownloading,
    getDownloadName,
    downloadNodeImage,
    downloadNodeVideo,
    t,
  ]);

  const nodeContextMenuItems = useMemo<MenuAction[]>(() => {
    if (!canDownloadNode) return contextMenuItems ?? [];
    return [
      ...(contextMenuItems ?? []),
      ...downloadMenuItems,
      ...(outputIds.length ? outputIds : [undefined]).map((outputId) => ({
        biEventKey: {
          menu: MENU_NAMES.FILE_VIEWER,
          action: outputIds.length ? 'copy_output_preview' : 'copy_preview',
        },
        name: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.COPY_IMAGE_PREVIEW, {
          name: outputIds.length > 1 ? getOutputHandle(nodeType as NodeType, outputId)?.label : 'image',
        }),
        action: () => copyNodeImage(outputId),
      })),
    ];
  }, [canDownloadNode, contextMenuItems, downloadMenuItems, outputIds, nodeType, copyNodeImage, t]);
  const hasContextMenuItems = nodeContextMenuItems.length > 0;
  const { handleContextMenuOpen, contextMenu } = useAppContextMenu({
    items: nodeContextMenuItems,
  });
  const handleContextMenu = hasContextMenuItems ? handleContextMenuOpen : undefined;
  const [showComparedNode, setShowComparedNode] = useState(false);

  useEffect(() => {
    if (canCompareInput) {
      flowView.setCompareInput(id, showComparedNode);
    }
  }, [showComparedNode, flowView, id, canCompareInput]);

  const overlay = useMemo(() => {
    if (hideOverlay) return null;

    return file?.type === 'image' || file?.type === 'video' || file?.type === '3D' || file?.type === 'audio' ? (
      <Overlay
        asset={{ ...file.value, type: file.type } as UploadedAsset}
        isHovered={isMediaFrameHovered}
        setShowComparedNode={canCompareInput ? setShowComparedNode : undefined}
        showComparedNode={showComparedNode}
        downloadMenuItems={downloadMenuItems.map((item) => ({
          ...item,
          biEventKey: item.biEventKey && { ...item.biEventKey, menu: MENU_NAMES.FILE_VIEWER_OVERLAY },
        }))}
      />
    ) : null;
  }, [hideOverlay, file, isMediaFrameHovered, showComparedNode, canCompareInput, downloadMenuItems]);

  return (
    <Box
      sx={{
        width: '100%',
        cursor,
        position: 'relative',
        ...(isDiagonalResizeEnabled
          ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
          : { height: '100%' }),
      }}
      className="nodrag"
    >
      {file?.type === 'video' ? (
        <VideoViewer
          id={id}
          disabled={disabled}
          clickToPlayDisabled={clickToPlayDisabled}
          overlay={overlay}
          handleContextMenu={handleContextMenu}
          onVideoPause={onVideoPause}
          onVideoScrub={onVideoScrub}
          width={file.value.width}
          height={file.value.height}
          duration={file.value.duration}
          fps={file.value.fps}
          hasAudio={file.value.hasAudio ?? true}
          noBorderRadius={noBorderRadius}
          mediaFrameProps={mediaFrameProps}
          frameOverlay={mediaFrameOverlay}
          playerControls={playerControls}
          timeDisplayMode={timeDisplayMode}
          onTimeDisplayModeChange={onTimeDisplayModeChange}
        />
      ) : file?.type === 'image' ? (
        <ImageViewer
          id={id}
          width={file.value.width}
          height={file.value.height}
          handleContextMenu={handleContextMenu}
          overlay={overlay}
          frameOverlay={mediaFrameOverlay}
          noBorderRadius={noBorderRadius}
          mediaFrameProps={mediaFrameProps}
        />
      ) : file?.type === '3D' ? (
        <ThreeDViewer
          id={id}
          handleContextMenu={handleContextMenu}
          overlay={overlay}
          mediaFrameProps={mediaFrameProps}
        />
      ) : file?.type === 'audio' && 'type' in file.value && file.value.type === 'computed' ? (
        audioEngineEnabled ? (
          <AudioNodeViewer
            id={id}
            duration={file.value.duration}
            overlay={overlay}
            mediaFrameProps={mediaFrameProps}
            playerControls={playerControls}
            timeDisplayMode={timeDisplayMode}
            onTimeDisplayModeChange={onTimeDisplayModeChange}
          />
        ) : (
          <ComputedAudioViewer
            id={id}
            duration={file.value.duration}
            overlay={overlay}
            mediaFrameProps={mediaFrameProps}
          />
        )
      ) : file?.type === 'audio' && 'url' in file.value ? (
        <AudioPreview
          key={file.value.url}
          url={assetUrl(file.value, resolvedAssets) ?? file.value.url}
          overlay={overlay}
          mediaFrameProps={mediaFrameProps}
        />
      ) : (
        <Box
          className="media-container-dark"
          sx={{ width: '100%', ...getMediaFramePlaceholderSx(nodeOutputType, isDiagonalResizeEnabled) }}
        />
      )}
      {hasContextMenuItems ? contextMenu : null}
    </Box>
  );
}

function Overlay(props: AssetOverlayProps) {
  return (
    <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, pointerEvents: 'none' }}>
      <GalleryGradientOverlay isSelected={props.isHovered}>
        <AssetOverlay isHovered={props.isHovered} {...props} />
      </GalleryGradientOverlay>
    </Box>
  );
}
